import slugify from "slugify";

/**
 * Pure helpers over an article's markdown. Kept out of the components so they
 * can be tested without rendering (src/lib/__tests__).
 */

const HEADING = /^#{2,6}\s+(.+?)\s*#*\s*$/;

/** "**Tokopedia**" -> "Tokopedia", "[x](y)" -> "x" */
function plainText(markdown) {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`~]/g, "")
    .replace(/<[^>]+>/g, "")
    .trim();
}

/**
 * Section headings for the table of contents, in document order.
 *
 * The body renders every heading level as h2 (the spec allows no h3), so all
 * of ## to ###### count. Ids get a `s-` prefix so they cannot collide with the
 * anchors older articles carry for hanamaru's price breakdown (#vat,
 * #price-jpy, ...), and repeats get a numeric suffix.
 */
export function extractHeadings(markdown) {
  const seen = {};
  let inFence = false;

  return markdown.split("\n").reduce((headings, line) => {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    const match = inFence ? null : line.match(HEADING);
    if (match === null) return headings;

    const text = plainText(match[1]);
    if (text === "") return headings;

    const base = `s-${slugify(text, { lower: true, remove: /[^\w\s-]/g }) || "bagian"}`;
    seen[base] = (seen[base] || 0) + 1;
    const id = seen[base] === 1 ? base : `${base}-${seen[base]}`;

    return headings.concat({ id, text });
  }, []);
}

/** Minutes to read at ~200 words a minute, never less than one. */
export function readingMinutes(markdown) {
  const words = plainText(markdown).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

/**
 * Ids ("category/slug") of the help articles this one links to, in order of
 * first appearance. Feeds "Artikel terkait": the articles the writer chose to
 * point at are the most related ones there are.
 */
export function linkedArticleIds(markdown) {
  const ids = [];
  const pattern = /\]\((?:https?:\/\/(?:www\.)?kyou\.id)?\/help\/(?!static\/|category\/|search)([a-z0-9-]+)\/([a-z0-9-]+)[^)]*\)/g;
  let match;

  while ((match = pattern.exec(markdown)) !== null) {
    const id = `${match[1]}/${match[2]}`;
    if (ids.indexOf(id) === -1) ids.push(id);
  }

  return ids;
}

/**
 * Up to `limit` related articles: first the ones the body links to, then the
 * same sub-topic, then the rest of the category -- nearest neighbours first, so
 * the panel suggests what sits next to this question rather than the top of
 * the list every time.
 */
export function relatedArticles(article, categories, limit = 4) {
  const byId = {};
  categories.forEach(category => category.articles.forEach(a => (byId[a.id] = a)));

  const category = categories.find(c => c.slug === article.category.slug);
  const siblings = category ? category.articles.filter(a => a.id !== article.id) : [];
  const position = category ? category.articles.findIndex(a => a.id === article.id) : -1;
  const byDistance = siblings
    .map(a => ({ a, distance: Math.abs(category.articles.indexOf(a) - position) }))
    .sort((x, y) => x.distance - y.distance)
    .map(entry => entry.a);

  const candidates = linkedArticleIds(article.content)
    .map(id => byId[id])
    .filter(Boolean)
    .concat(byDistance.filter(a => a.subcategory === article.subcategory))
    .concat(byDistance);

  return candidates.reduce((picked, candidate) => {
    if (picked.length < limit && candidate.id !== article.id && picked.indexOf(candidate) === -1) picked.push(candidate);
    return picked;
  }, []);
}
